import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  ColDef,
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  GridReadyEvent,
  ICellRendererParams,
  IServerSideGetRowsParams,
  PaginationChangedEvent,
} from '@ag-grid-community/core'
import { ModuleRegistry } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import { Button, Modal, Select } from '@surecompanies/core_components'
import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import { relativeTimeString } from 'enrollment-platform/utils/relative-time-string'
import { PlanOverrideAction } from '../../../enums/plan-override-action.enum'
import type {
  ServiceAreaLevelHistoryEntryDto,
  ServiceAreaLevelHistoryQueryDto,
} from '../../../api/dto/service-area-level-history.dto'
import {
  listServiceAreaLevelHistory,
  SERVICE_AREA_OVERRIDE_RULE_BASE,
} from '../../../api/plan-service-area-override-rules-api'
import { useDeleteHistoryEntry } from '../../../hooks/useDeleteHistoryEntry'
import { useDeleteHistoryByFilter } from '../../../hooks/useDeleteHistoryByFilter'
import { US_STATE_OPTIONS } from '../../../constants'
import styles from '../../../components/PlanOverridesComponents.module.scss'

ModuleRegistry.registerModules([ServerSideRowModelModule])

const DEFAULT_PAGE_SIZE = 20
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

const ACTION_LABELS: Partial<Record<PlanOverrideAction, string>> = {
  [PlanOverrideAction.OVERRIDE_CREATED]: 'Created',
  [PlanOverrideAction.OVERRIDE_UPDATED]: 'Updated',
  [PlanOverrideAction.OVERRIDE_STATUS_CHANGED]: 'Status changed',
  [PlanOverrideAction.OVERRIDE_DELETED]: 'Deleted',
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function formatActorName(name: string | null): string {
  if (!name) return '—'
  if (UUID_RE.test(name)) return `${name.slice(0, 8)}…`
  return name
}

const ActionCellRenderer = ({
  data,
}: ICellRendererParams<ServiceAreaLevelHistoryEntryDto>) => {
  if (!data) return null
  const known = ACTION_LABELS[data.action as PlanOverrideAction]
  const isDocument = (data.action as string).includes('DOCUMENT')
  const label = known ?? (isDocument ? 'Document' : 'Other')
  return <span>{label}</span>
}

const ActorCellRenderer = ({
  data,
}: ICellRendererParams<ServiceAreaLevelHistoryEntryDto>) => {
  if (!data) return null
  return (
    <span className={styles.historyActorCell}>
      {formatActorName(data.actorName)}
    </span>
  )
}

const DEFAULT_COL_DEF: ColDef = {
  sortable: false,
  filter: false,
  resizable: true,
}

function getParamString(
  params: Record<string, unknown> | undefined,
  key: string
): string {
  const value = params?.[key]
  return typeof value === 'string' && value.trim() ? value : '—'
}

function getParamBoolean(
  params: Record<string, unknown> | undefined,
  key: string
): boolean | undefined {
  const value = params?.[key]
  return typeof value === 'boolean' ? value : undefined
}

interface Props {
  onTotalChange?: (total: number) => void
  defaultPageSize?: number
  onPageSizeChange?: (size: number) => void
}

export default function ServiceAreaLevelHistoryContent({
  onTotalChange,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  onPageSizeChange,
}: Props) {
  const [stateFilter, setStateFilter] = useState<string | undefined>(undefined)

  const filters = useMemo<ServiceAreaLevelHistoryQueryDto>(
    () => ({
      state: stateFilter,
    }),
    [stateFilter]
  )

  const [total, setTotal] = useState(0)

  const onTotalChangeRef = useRef(onTotalChange)
  useEffect(() => {
    onTotalChangeRef.current = onTotalChange
  }, [onTotalChange])

  const filtersRef = useRef(filters)
  useEffect(() => {
    filtersRef.current = filters
  }, [filters])

  const [grid, setGrid] =
    useState<GridReadyEvent<ServiceAreaLevelHistoryEntryDto>>()
  const gridInitializedRef = useRef(false)

  const { mutateAsync: deleteEntry } = useDeleteHistoryEntry(
    SERVICE_AREA_OVERRIDE_RULE_BASE
  )
  const { mutateAsync: deleteByFilter } = useDeleteHistoryByFilter(
    SERVICE_AREA_OVERRIDE_RULE_BASE
  )

  const handleDeleteRow = useCallback(
    (logId: string) => {
      Modal.confirm({
        title: 'Delete history entry?',
        content: 'This action cannot be undone.',
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: async () => {
          await deleteEntry(logId)
          grid?.api.refreshServerSide({ purge: true })
        },
      })
    },
    [deleteEntry, grid]
  )

  const handleDeleteByFilter = useCallback(() => {
    const hasFilters = Object.values(filtersRef.current).some(
      (v) => v !== undefined
    )
    Modal.confirm({
      title: 'Delete history entries?',
      content: hasFilters
        ? 'Delete all matching history entries? This action cannot be undone.'
        : 'Delete ALL history entries? This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        await deleteByFilter(filtersRef.current as Record<string, unknown>)
        grid?.api.refreshServerSide({ purge: true })
      },
    })
  }, [deleteByFilter, grid])

  const handlePaginationChanged = useCallback(
    (event: PaginationChangedEvent<ServiceAreaLevelHistoryEntryDto>) => {
      if (event.newPageSize && gridInitializedRef.current) {
        onPageSizeChange?.(event.api.paginationGetPageSize())
      }
    },
    [onPageSizeChange]
  )

  const datasource = useMemo(
    () => ({
      getRows: async (
        params: IServerSideGetRowsParams<ServiceAreaLevelHistoryEntryDto>
      ) => {
        try {
          const { startRow, endRow } = params.request
          const pageSize = (endRow ?? DEFAULT_PAGE_SIZE) - (startRow ?? 0)
          const page = Math.floor((startRow ?? 0) / pageSize) + 1

          const result = await listServiceAreaLevelHistory({
            ...filtersRef.current,
            page,
            limit: pageSize,
          })

          onTotalChangeRef.current?.(result.meta.total)
          setTotal(result.meta.total)
          params.success({
            rowData: result.data,
            rowCount: result.meta.total,
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to load service area level history', error)
          params.fail()
        }
      },
    }),
    [filters]
  )

  useEffect(() => {
    if (!grid) return
    grid.api.setGridOption('serverSideDatasource', datasource)
    grid.api.refreshServerSide({ purge: true })
  }, [grid, datasource])

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<ServiceAreaLevelHistoryEntryDto>) => [
      {
        name: 'Copy Cell',
        action: () => {
          const value = params.value
          if (value != null) void navigator.clipboard.writeText(String(value))
        },
      },
    ],
    []
  )

  const handleFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent<ServiceAreaLevelHistoryEntryDto>) => {
      gridInitializedRef.current = true
      event.api.autoSizeAllColumns(false)
    },
    []
  )

  const columnDefs = useMemo<ColDef<ServiceAreaLevelHistoryEntryDto>[]>(
    () => [
      {
        colId: 'action',
        headerName: 'Action',
        field: 'action',
        cellRenderer: ActionCellRenderer,
        minWidth: 130,
        pinned: 'left',
        lockPinned: true,
        suppressMovable: true,
      },
      {
        colId: 'updatedAt',
        headerName: 'Updated',
        field: 'occurredAt',
        valueFormatter: (p) =>
          p.value ? relativeTimeString(p.value as string, 30) : '—',
        sort: 'desc',
        minWidth: 160,
        pinned: 'left',
        lockPinned: true,
        suppressMovable: true,
      },
      {
        colId: 'year',
        headerName: 'Year',
        field: 'matchYear',
        minWidth: 80,
      },
      {
        colId: 'state',
        headerName: 'State',
        field: 'matchState',
        minWidth: 80,
      },
      {
        colId: 'issuerId',
        headerName: 'Issuer ID',
        field: 'matchIssuerId',
        minWidth: 100,
      },
      {
        colId: 'hiosPlanId',
        headerName: 'HIOS Plan ID',
        field: 'matchHiosPlanId',
        minWidth: 130,
      },
      {
        colId: 'serviceAreaId',
        headerName: 'Service Area ID',
        field: 'matchServiceAreaId',
        minWidth: 120,
      },
      {
        colId: 'countyCode',
        headerName: 'County Code',
        field: 'matchCountyCode',
        minWidth: 100,
      },
      {
        colId: 'zipcode',
        headerName: 'Zipcode',
        field: 'matchZipcode',
        minWidth: 90,
      },
      {
        colId: 'sourceName',
        headerName: 'Source Name',
        valueGetter: (p) => getParamString(p.data?.params, 'sourceName'),
        minWidth: 120,
      },
      {
        colId: 'serviceAreaName',
        headerName: 'Service Area Name',
        valueGetter: (p) => getParamString(p.data?.params, 'serviceAreaName'),
        minWidth: 200,
      },
      {
        colId: 'isActive',
        headerName: 'Is Active',
        valueGetter: (p) => {
          const isActiveParam = getParamBoolean(p.data?.params, 'isActive')
          if (typeof isActiveParam === 'boolean') {
            return isActiveParam ? 'Yes' : 'No'
          }
          return p.data?.status === 'APPROVED'
            ? 'Yes'
            : p.data?.status === 'DRAFT' || p.data?.status === 'REJECTED'
              ? 'No'
              : '—'
        },
        minWidth: 80,
      },
      {
        colId: 'updatedBy',
        headerName: 'Updated by',
        cellRenderer: ActorCellRenderer,
        minWidth: 220,
      },
      {
        colId: 'rowActions',
        headerName: '',
        width: 52,
        resizable: false,
        sortable: false,
        cellRenderer: ({
          data,
        }: ICellRendererParams<ServiceAreaLevelHistoryEntryDto>) => {
          if (!data) return null
          return (
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteRow(data.id)}
            />
          )
        },
      },
    ],
    [handleDeleteRow]
  )

  return (
    <>
      <div className={styles.historyFilterBar}>
        {total > 0 && (
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleDeleteByFilter}
            style={{ marginRight: 'auto' }}
          >
            Delete
          </Button>
        )}
        <Select
          placeholder="Filter by state"
          allowClear
          showSearch
          optionFilterProp="label"
          options={US_STATE_OPTIONS}
          value={stateFilter}
          onChange={(v: string | undefined) => setStateFilter(v)}
          className={styles.historyFilterStateSelect}
        />
      </div>

      <div className={`ag-theme-alpine ${styles.historyGridAutoHeight}`}>
        <AgGridReact
          getRowId={({ data }) => data.id}
          rowModelType="serverSide"
          columnDefs={columnDefs}
          defaultColDef={DEFAULT_COL_DEF}
          enableCellTextSelection={false}
          suppressRowClickSelection
          getContextMenuItems={getContextMenuItems}
          overlayLoadingTemplate="Loading..."
          overlayNoRowsTemplate="No history entries yet"
          domLayout="autoHeight"
          pagination
          paginationPageSize={defaultPageSize}
          paginationPageSizeSelector={PAGE_SIZE_OPTIONS}
          cacheBlockSize={defaultPageSize}
          suppressColumnVirtualisation={true}
          onFirstDataRendered={handleFirstDataRendered}
          onPaginationChanged={handlePaginationChanged}
          onGridReady={setGrid}
        />
      </div>
    </>
  )
}
